import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewVoucherReport } from "http-hooks/voucher/useVoucher";
import Select from "components/ui/Select";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import Modal from "components/ui/modals";

// Constants for options
const reconOptions = [
  { value: "0", label: "Without Recon" },
  { value: "1", label: "With Recon" }
];

const coverLetterOptions = [
  { value: "0", label: "No" },
  { value: "1", label: "Yes" }
];

export default function PrintVoucher({ voucher, onClose, open }) {
  const [selectedRecon, setSelectedRecon] = useState(reconOptions[0]);
  const [selectedCoverLetter, setSelectedCoverLetter] = useState(coverLetterOptions[0]);
  const { mutateAsync: viewReport, isPending } = useViewVoucherReport();

  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Build query parameters for Money Receipt options
      const queryParams = new URLSearchParams({
        select_boolrecon: selectedRecon.value,
        select_boolcoverletter: selectedCoverLetter.value
      }).toString();
      
      const data = await viewReport(`${voucher.trid}?${queryParams}`);
      
      if (data.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);
        
        const route = getDocumentViewerRoute(voucher.trid, {
          htmlUrl,
          pdfUrl,
          documentNumber: voucher.docno,
          documentType: 'Money Receipt'
        });
        
        navigate(route);
        onClose();
      }
    } catch (error) {
      console.error('Error viewing report:', error);
    }
  };

  const showReconOption = voucher.trtyp === 'MR';
  const showCoverLetterOption = voucher.trtyp === 'MR';

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          Print {voucher.trtyp === 'MR' ? 'Money Receipt' : 'Journal Voucher'} Voucher
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="print-voucher-form"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isPending}
          >
            {isPending ? 'Generating...' : 'Generate Print'}
          </button>
        </div>
      }
    >
      <form id="print-voucher-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-md space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="font-medium text-gray-700">Voucher No:</div>
            <div className="text-gray-900">{voucher.docno}</div>
            <div className="font-medium text-gray-700">Date:</div>
            <div className="text-gray-900">
              {voucher.trdate && new Date(voucher.trdate).toLocaleDateString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric'
              })}
            </div>
            <div className="font-medium text-gray-700">Debit Account:</div>
            <div className="text-gray-900">{voucher.debitAccount?.name || voucher.acdebit}</div>
            <div className="font-medium text-gray-700">Credit Account:</div>
            <div className="text-gray-900">{voucher.creditAccount?.name || voucher.accredit}</div>
            <div className="font-medium text-gray-700">Amount:</div>
            <div className="text-gray-900 font-semibold">
              Rs. {new Intl.NumberFormat('en-IN', {
                minimumFractionDigits: 2, maximumFractionDigits: 2
              }).format(voucher.tramount)}
            </div>
          </div>
        </div>
        {showReconOption && (
          <Select
            value={selectedRecon}
            onChange={setSelectedRecon}
            options={reconOptions}
            label="With/Without Recon"
            placeholder="Select option"
            isClearable={false}
          />
        )}
        {showCoverLetterOption && (
          <Select
            value={selectedCoverLetter}
            onChange={setSelectedCoverLetter}
            options={coverLetterOptions}
            label="Include Cover Letter"
            placeholder="Select option"
            isClearable={false}
          />
        )}
      </form>
    </Modal>
  );
}

